import {
    ChangeDetectionStrategy,
    Component,
    inject,
    Input,
    OnInit,
    output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { IForumPostEdit } from '@fi-shared/fi-forum/fi-forum';
import { ICommentModel } from '@fi-shared/fi-comments/fi-comments';
import { ModuleType, ObjectTypes } from '@fi-shared/types';
import { BehaviorSubject } from 'rxjs';
import { FiProfileSizes } from '@fi-shared/profile-icon/profile-icon.component';
import { FiCommentAddComponent } from '@fi-shared/fi-comments/fi-comments-list/fi-comment-add/fi-comment-add.component';
import { cloneDeep } from '@fi-shared/utils';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { StatisticService } from '@fi-shared/statistic/statistic.service';
import { AppTextService } from '@fi-shared/app-text';
import { FiForumSharedService } from '@fi-shared/fi-forum/fi-forum-shared.service';
import { FiSignalRHelperService } from '@fi-shared/fi-signal-r/fi-signal-r-helper.service';
import { FiNotificationHelperService } from '@fi-shared/fi-notification/fi-notification-helper.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiItemsDeleteHelperService } from '@fi-shared/fi-shared-services/fi-items-delete-helper.service';
import { FiCommentsListStateService } from '@fi-shared/fi-comments/fi-comments-list/services/fi-comments-list-state.service';
import { TextEditorTagUsersService } from '@fi-shared/fi-text-editor/fi-text-editor-helper-services/text-editor-tag-users.service';

@Component({
    selector: 'fi-forum-post-view',
    templateUrl: './fi-forum-post-view.component.html',
    styleUrls: ['./fi-forum-post-view.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-forum-post-view' },
    standalone: false,
})
export class FiForumPostViewComponent implements OnInit {
    @Input() forumPost: IForumPostEdit;
    @Input() comments: ICommentModel[];
    @Input() isEditMode: boolean;
    @Input() scrollToCommentId: number;
    public forumPostEdited = output<IForumPostEdit>();

    protected showEditor$ = new BehaviorSubject<boolean>(false);

    @ViewChild(FiCommentAddComponent) commentAddComponent: FiCommentAddComponent;
    protected forumPostCopyForEdit: IForumPostEdit;

    private fiStateService = inject(FiStateService);
    private statisticService = inject(StatisticService);
    private appTextService = inject(AppTextService);
    private forumService = inject(FiForumSharedService);
    private notificationHelper = inject(FiNotificationHelperService);
    private fiSignalRHelperService = inject(FiSignalRHelperService);
    private fiItemsDeleteHelperService = inject(FiItemsDeleteHelperService);
    private commentsListStateService = inject(FiCommentsListStateService);
    private textEditorTagUsersService = inject(TextEditorTagUsersService);

    public ngOnInit(): void {
        this.statisticService.Visit(ModuleType.ForumPost, ObjectTypes.ForumPost, this.forumPost.Id);

        if (this.notificationHelper.isNotVisited(this.forumPost)) {
            this.forumService
                .markForumPostsAsVisited(this.forumPost.ForumId, [this.forumPost.Id])
                .then((forumPostIdsMarkedAsVisited) => {
                    this.notificationHelper.setIsVisited(this.forumPost);
                    this.fiSignalRHelperService.onMarkForumPostsAsVisited(forumPostIdsMarkedAsVisited);
                });
        }
        this.updateContentWithFixedUserProfileTags();

        if (this.isEditMode) {
            this.toggleEdit();
        }
    }

    private updateContentWithFixedUserProfileTags(): void {
        const updatedContent = this.textEditorTagUsersService.getContentWithFixedUserProfileTags(
            this.forumPost.Content,
            this.forumPost.TaggedUsers,
        );
        this.forumPost.Content = updatedContent;
    }

    protected onReplyClick(): void {
        this.commentAddComponent.setFocus();
    }

    protected isEditActionEnabled(): boolean {
        return this.forumPost.HasEditAccess;
    }

    protected onForumPostSaved($event: ICommentModel): void {
        // after edit we should copy all properties of saved forum post to original one
        for (let prop in $event) {
            this.forumPost[prop] = $event[prop];
        }
        this.forumPost.ChangedOn = new Date().toISOString();
        this.updateContentWithFixedUserProfileTags();
        this.showEditor$.next(false);
        this.forumPostEdited.emit(this.forumPost);
    }

    protected onForumPostEditCancelled(): void {
        this.showEditor$.next(false);
    }

    protected toggleEdit($event?: MouseEvent): void {
        if ($event) {$event.preventDefault();}

        this.showEditor$.next(!this.showEditor$.value);
        if (this.showEditor$.value) {
            this.forumPostCopyForEdit = cloneDeep(this.forumPost);
        }
    }

    protected confirmForumPostDelete(): void {
        this.fiItemsDeleteHelperService
            .commonDeleteConfirm(ObjectTypes.ForumPost, [this.forumPost])
            .then(() => this.deleteForumPost());
    }

    private deleteForumPost() {
        this.forumService.deleteForumPosts([this.forumPost.Id]).then(() => {
            this.fiStateService.go(FiStateNames.Forum.View, { id: this.forumPost.ForumId });
        });
    }

    protected getRepliesCountText(commentsCount: number): string {
        return (
            commentsCount + ' ' + this.appTextService.get(commentsCount !== 1 ? 'common.comments' : 'common.comment')
        );
    }

    protected get commentsCount(): number {
        return this.commentsListStateService.getCommentsCount(ObjectTypes.ForumPost, this.forumPost.Id);
    }

    protected readonly ObjectTypes = ObjectTypes;
    protected readonly ProfileSizes = FiProfileSizes;
}
